import clsx from "clsx";
import { useId, type InputHTMLAttributes } from "react";

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** The question or name in words, shown above the box. */
  label: string;
  /** Help under the label. */
  hint?: string;
  /** Why the box was refused, in plain words. */
  error?: string | null;
}

/** A labelled text box. The label, the hint and the error are tied to the box for screen readers. */
export function TextField({ label, hint, error, id, className, ...rest }: TextFieldProps) {
  const auto = useId();
  const inputId = id ?? auto;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={clsx("field", className)}>
      <label htmlFor={inputId} className="field__label">
        {label}
      </label>
      {hint && (
        <p id={hintId} className="field__hint muted">
          {hint}
        </p>
      )}
      <input id={inputId} className="field__input" aria-describedby={describedBy} aria-invalid={error ? true : undefined} {...rest} />
      {error && (
        <p id={errorId} role="alert" className="field__error">
          {error}
        </p>
      )}
    </div>
  );
}
